import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient.js';

// Find the next Monday after currentStartDate, skipping stat holidays
const getNextWeekStart = (currentStartDate, holidays) => {
  const d = new Date(currentStartDate + 'T00:00:00');
  const dayOfWeek = d.getDay(); // 0=Sun, 1=Mon, ...
  // Always advance to next Monday (at least 1 day forward, 7 if already Monday)
  const daysToNextMonday = ((1 - dayOfWeek + 7) % 7) || 7;
  const candidate = new Date(d);
  candidate.setDate(candidate.getDate() + daysToNextMonday);

  // Skip stat holidays and weekends (safety — Monday shouldn't be a weekend, but handle consecutive holidays)
  const holidayDates = new Set(holidays.map((h) => h.holiday_date));
  while (
    holidayDates.has(candidate.toISOString().split('T')[0]) ||
    candidate.getDay() === 0 ||
    candidate.getDay() === 6
  ) {
    candidate.setDate(candidate.getDate() + 1);
  }
  return candidate.toISOString().split('T')[0];
};

// Find the first Monday on or after a given date, skipping holidays
const getNextMondayOnOrAfter = (date, holidays) => {
  const d = new Date(date);
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  const holidayDates = new Set(holidays.map((h) => h.holiday_date));
  while (
    holidayDates.has(d.toISOString().split('T')[0]) ||
    d.getDay() === 0 ||
    d.getDay() === 6
  ) {
    d.setDate(d.getDate() + 1);
  }
  return d.toISOString().split('T')[0];
};

const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const datesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

const fmt = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const PushRouteModal = ({ route, items, holidays, onClose, onPushed }) => {
  const [pushPlan, setPushPlan] = useState(null); // null = loading, [] = computed
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState(null);

  const maxDayNumber = Math.max(...items.filter((i) => i.day_number).map((i) => i.day_number), 0);
  const pinnedCount = items.filter((i) => i.is_pinned).length;

  useEffect(() => {
    const computePlan = async () => {
      const newStart = getNextWeekStart(route.start_date, holidays);
      const maxDay = maxDayNumber || 5;
      const newEnd = addDays(newStart, maxDay - 1);

      const plan = [{
        routeId: route.id,
        routeName: route.route_name,
        oldStart: route.start_date,
        newStart,
        newEnd,
        maxDay,
        pinnedCount,
        isPrimary: true,
      }];

      // Check for cascade if route has an applicator
      if (route.applicator_id) {
        const { data: otherRoutes } = await supabase
          .from('routes')
          .select('id, route_name, start_date')
          .eq('applicator_id', route.applicator_id)
          .neq('id', route.id)
          .in('status', ['draft', 'active'])
          .not('start_date', 'is', null);

        if (otherRoutes && otherRoutes.length > 0) {
          // Fetch items for max day_number + pinned count
          const { data: otherItems } = await supabase
            .from('route_items')
            .select('route_id, day_number, is_pinned')
            .in('route_id', otherRoutes.map((r) => r.id));

          const routeInfo = {};
          for (const r of otherRoutes) {
            routeInfo[r.id] = { maxDay: 0, pinnedCount: 0 };
          }
          for (const item of otherItems || []) {
            const info = routeInfo[item.route_id];
            if (info) {
              if (item.day_number > info.maxDay) info.maxDay = item.day_number;
              if (item.is_pinned) info.pinnedCount++;
            }
          }

          // Iterative cascade detection
          const pushed = new Set([route.id]);
          let changed = true;
          while (changed) {
            changed = false;
            for (const other of otherRoutes) {
              if (pushed.has(other.id)) continue;

              const otherMax = routeInfo[other.id].maxDay || 5;
              const otherStart = other.start_date;
              const otherEnd = addDays(otherStart, otherMax - 1);

              // Check against all routes already in the push plan
              for (const entry of plan) {
                if (datesOverlap(entry.newStart, entry.newEnd, otherStart, otherEnd)) {
                  // Push this route to the first Monday after the conflicting route's new end
                  const dayAfterEnd = addDays(entry.newEnd, 1);
                  const cascadeStart = getNextMondayOnOrAfter(dayAfterEnd, holidays);
                  const cascadeEnd = addDays(cascadeStart, otherMax - 1);

                  plan.push({
                    routeId: other.id,
                    routeName: other.route_name,
                    oldStart: other.start_date,
                    newStart: cascadeStart,
                    newEnd: cascadeEnd,
                    maxDay: otherMax,
                    pinnedCount: routeInfo[other.id].pinnedCount,
                    isPrimary: false,
                  });
                  pushed.add(other.id);
                  changed = true;
                  break;
                }
              }
              if (changed) break; // restart outer loop
            }
          }
        }
      }

      setPushPlan(plan);
    };

    computePlan().catch((err) => {
      console.error('Error computing push plan:', err);
      setError('Failed to check for conflicts.');
    });
  }, []);

  const totalPinnedCount = pushPlan
    ? pushPlan.reduce((sum, entry) => sum + entry.pinnedCount, 0)
    : 0;

  const handleConfirm = async () => {
    setPushing(true);
    setError(null);

    try {
      for (const entry of pushPlan) {
        const { error: routeError } = await supabase
          .from('routes')
          .update({ start_date: entry.newStart, needs_travel_calc: true })
          .eq('id', entry.routeId);

        if (routeError) {
          setError(`Failed to push "${entry.routeName}": ${routeError.message}`);
          setPushing(false);
          return;
        }

        if (entry.pinnedCount > 0) {
          await supabase
            .from('route_items')
            .update({ pin_needs_review: true })
            .eq('route_id', entry.routeId)
            .eq('is_pinned', true);
        }
      }

      onPushed();
    } catch (err) {
      setError(`Push failed: ${err.message}`);
      setPushing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Push Route Forward</h3>

        {error && (
          <div className="bg-red-500/20 border border-red-500/40 text-red-200 rounded p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {!pushPlan ? (
          <p className="text-sm text-gray-400">Checking for conflicts...</p>
        ) : (
          <div className="space-y-4">
            {/* Primary route */}
            <div className="text-sm text-gray-300">
              <span className="font-medium text-white">"{pushPlan[0].routeName}"</span> will move
              from{' '}
              <span className="text-gray-400">{fmt(pushPlan[0].oldStart)}</span> to{' '}
              <span className="text-green-400 font-medium">{fmt(pushPlan[0].newStart)}</span>
            </div>

            {/* Cascade routes */}
            {pushPlan.length > 1 && (
              <div className="bg-gray-800 rounded p-3">
                <p className="text-xs font-medium text-gray-400 mb-2">
                  The following routes will also push to avoid overlap:
                </p>
                <div className="space-y-1.5">
                  {pushPlan.slice(1).map((entry) => (
                    <div key={entry.routeId} className="text-sm text-gray-300">
                      <span className="font-medium text-white">"{entry.routeName}"</span>{' '}
                      <span className="text-gray-400">{fmt(entry.oldStart)}</span>{' '}
                      <span className="text-gray-400">&rarr;</span>{' '}
                      <span className="text-green-400">{fmt(entry.newStart)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pinned items warning */}
            {totalPinnedCount > 0 && (
              <div className="bg-amber-400/10 border border-amber-400/30 text-amber-300 rounded p-3 text-sm">
                {totalPinnedCount} pinned job{totalPinnedCount !== 1 ? 's' : ''} across{' '}
                {pushPlan.filter((e) => e.pinnedCount > 0).length} route{pushPlan.filter((e) => e.pinnedCount > 0).length !== 1 ? 's' : ''}{' '}
                will be flagged for review. Their calendar dates will shift.
              </div>
            )}

            {/* Travel note */}
            <p className="text-xs text-gray-400">
              Travel times will be marked for recalculation.
            </p>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={pushing}
                className="px-4 py-2 text-sm font-medium rounded-md bg-green-700 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {pushing ? 'Pushing...' : 'Push Forward'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushRouteModal;
